"use client";

import Link from "next/link";
import { Loader2, Rocket } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { formatHirePrice } from "~/lib/marketplace/hire-pricing";
import {
  summarizeRecommendationsPricing,
  type HireRecommendationsSelection,
  type PricingLineItem,
} from "~/lib/marketplace/hire-recommendations-pricing";
import { buildProjectDashboardProfileUrl } from "~/lib/auth-paths";

import type { HireOrganizationLayoutMode } from "~/lib/marketplace/hire-organization-layout";
import {
  AI_EMPLOYEE_PLANS,
  type AiEmployeePlanId,
} from "~/lib/siteglue/ai-employee-plans";

type HireWizardSummaryStepProps = {
  businessName: string;
  agentName: string;
  selection: HireRecommendationsSelection;
  totalLabel: string;
  isFree: boolean;
  previewReady: boolean;
  /** When false, primary Onboard button is disabled until Trish finishes. */
  hireEnabled?: boolean;
  isGenerating: boolean;
  isSubmitting: boolean;
  deployedProjectId: string | null;
  accountLabel?: string;
  organizationLayout?: HireOrganizationLayoutMode;
  productLabel?: string;
  siteGluePlanId?: AiEmployeePlanId | null;
  onGeneratePreview?: () => void;
  onHire: () => void;
  className?: string;
};

function SummarySection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h4>
      <div className="rounded-md border border-slate-100 bg-white px-3 py-2 text-sm text-slate-700">
        {children}
      </div>
    </div>
  );
}

function enabledSkills(selection: HireRecommendationsSelection): string[] {
  const skills: string[] = [];
  if (selection.liveChat) skills.push("Live Chat");
  if (selection.textMessages) skills.push("Text Messages");
  if (selection.email) skills.push("Email");
  if (selection.browserCalls) skills.push("Voice (WebRTC)");
  if (selection.videoCalls) skills.push("Video Calls");
  if (selection.phoneCalls) skills.push("Phone Calls");
  if (selection.welcomeVideo) skills.push("Welcome Video");
  if (selection.messenger) skills.push("Messenger");
  if (selection.whatsapp) skills.push("WhatsApp");
  return skills;
}

export function HireWizardSummaryStep({
  businessName,
  agentName,
  selection,
  totalLabel,
  isFree,
  previewReady,
  hireEnabled = true,
  isGenerating,
  isSubmitting,
  deployedProjectId,
  accountLabel,
  organizationLayout = "locations",
  productLabel = "Inbound GPT",
  siteGluePlanId = null,
  onGeneratePreview,
  onHire,
  className,
}: HireWizardSummaryStepProps) {
  const lineItems: PricingLineItem[] = summarizeRecommendationsPricing(selection);
  const skills = enabledSkills(selection);
  const siteGluePlan = siteGluePlanId
    ? AI_EMPLOYEE_PLANS.find((p) => p.id === siteGluePlanId)
    : null;
  const isSiteGlue = Boolean(siteGluePlanId) || productLabel === "SiteGlue";
  const onboardLabel = isSiteGlue
    ? `Onboard ${agentName?.trim() || "Your AI Employee"}`
    : `Onboard ${agentName}`;

  const onboardButton = (
    <Button
      type="button"
      className="w-full"
      disabled={isSubmitting || !hireEnabled}
      onClick={onHire}
    >
      {isSubmitting ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Processing…
        </>
      ) : (
        <>
          <Rocket className="mr-2 h-4 w-4" />
          {onboardLabel}
          {!isFree ? ` · ${totalLabel}` : ""}
        </>
      )}
    </Button>
  );

  return (
    <div className={cn("space-y-6", className)}>
      {isSiteGlue ? (
        <div className="space-y-2 rounded-xl border border-[#C3D4FC] bg-[#F0F5FF] p-4">
          <p className="text-sm font-semibold text-slate-900">
            Ready to hire {agentName?.trim() || "your AI Employee"}?
          </p>
          <p className="text-xs text-slate-600">
            Review your proposal below, then continue with onboarding.
          </p>
          {onboardButton}
          {!hireEnabled ? (
            <p className="text-center text-xs text-slate-500">
              Finish with Trish first — then you can onboard here.
            </p>
          ) : null}
        </div>
      ) : null}
      <div>
        <h3 className="text-base font-semibold text-slate-900">
          {isSiteGlue ? "Your AI Employee Proposal" : `${productLabel} Summary`}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {isSiteGlue
            ? "Review your AI Employee’s role, channels, organization setup, training, tools, and career path before onboarding."
            : `Review your configuration for ${businessName} before onboarding ${agentName}.`}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <SummarySection title={isSiteGlue ? "Organization" : "Company"}>
          <p className="font-medium text-slate-900">{businessName}</p>
          {accountLabel ? <p className="mt-1 text-xs text-slate-500">{accountLabel}</p> : null}
        </SummarySection>

        <SummarySection title="Agent">
          <p className="font-medium text-slate-900">{agentName}</p>
        </SummarySection>

        <SummarySection title={isSiteGlue ? "My Channels" : "Responsibilities"}>
          <p>{skills.length > 0 ? skills.join(", ") : "None selected"}</p>
        </SummarySection>

        <SummarySection title="Training">
          <p>
            Basic
            {selection.agentTrainingAdvanced ? " + Advanced" : ""}
          </p>
        </SummarySection>

        <SummarySection title={isSiteGlue ? "Locations & Team" : "Organization"}>
          <p>
            {organizationLayout === "departments"
              ? `${selection.departmentCount} department${selection.departmentCount === 1 ? "" : "s"}, 1 location`
              : `${selection.locationCount} location${selection.locationCount === 1 ? "" : "s"}, 1 department`}
            , {selection.teamMemberCount} team member
            {selection.teamMemberCount === 1 ? "" : "s"}
          </p>
        </SummarySection>

        <SummarySection title="Tools">
          <p>
            {[
              selection.appointmentsEnabled ? "Scheduling" : null,
              selection.emrIntegrationEnabled ? "EMR" : null,
            ]
              .filter(Boolean)
              .join(", ") || "None"}
          </p>
        </SummarySection>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {isSiteGlue ? "Career Path" : "Monthly subscription"}
        </p>
        {isSiteGlue && siteGluePlan ? (
          <div className="mt-3 space-y-2 text-sm text-slate-700">
            <p>
              <span className="font-semibold text-slate-900">{siteGluePlan.name}</span>
              {" — "}
              {siteGluePlan.tagline}
            </p>
            <ul className="list-inside list-disc space-y-1 text-slate-600">
              <li>Intern — Free · Website Chat</li>
              <li>Associate — $49/month · Expanded communication channels and capabilities</li>
              <li>Professional — $99/month · Advanced channels, automations, and integrations</li>
            </ul>
          </div>
        ) : isFree ? (
          <p className="mt-2 text-sm text-slate-600">
            Live Chat and basic setup are included at no monthly cost.
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {lineItems.map((item) => (
              <li key={item.label} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-slate-700">{item.label}</span>
                <span className="font-medium text-slate-900">
                  {formatHirePrice(item.amountCents)}/mo
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3">
          <span className="text-sm font-semibold text-slate-900">Total</span>
          <span className="text-lg font-bold text-slate-900">{totalLabel}</span>
        </div>
      </div>

      {!previewReady && onGeneratePreview ? (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={isGenerating}
          onClick={onGeneratePreview}
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating preview…
            </>
          ) : (
            "Generate agent preview"
          )}
        </Button>
      ) : null}

      {!isSiteGlue ? onboardButton : (
        <div className="space-y-2 border-t border-slate-200 pt-4">
          {onboardButton}
        </div>
      )}

      {deployedProjectId ? (
        <p className="text-center text-xs text-slate-500">
          <Link
            href={buildProjectDashboardProfileUrl(deployedProjectId)}
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            Open project dashboard
          </Link>
        </p>
      ) : (
        <p className="text-center text-xs text-slate-500">
          {!hireEnabled
            ? isSiteGlue
              ? "Finish with Trish first — then you can onboard here."
              : "Finish with Trish first — then you can onboard here."
            : isFree
              ? "Free setup — go straight to your agent dashboard."
              : "You'll confirm payment on secure Stripe checkout."}
        </p>
      )}
    </div>
  );
}
